import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { Box, Button, useTheme, Grid2 } from "@mui/material";
import CustomTypography from "../../components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../components/common/textFields/DatePickerRangeNew";
import { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";

import AppDropDown from "../../components/common/filter/AppDropDown";
import AppButton from "../../components/common/buttons/AppButton";
import { useNotification } from "../../../utils/NotificationsContext";
import LoaderOverlay from "../../components/common/pages/AppLoader";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { SortType } from "../../../models/SortType";
import { FilterValue } from "../../../models/Filters/FilterValue";
import {
  parseQueryParamsForUnpaidSamplesAnalyticsFilters,
  UnpaidSamplesAnalyticsFilters,
  UnpaidSamplesAnalyticsSelectedFilters,
} from "../../../models/Dashboard/UnpaidSamplesDashboard/UnpaidSamplesAnalyticsDashboardAPIFilters";
import TypeFilterCard from "../../components/filters/TypeFilterCard";
import DashboardUnpaidSamplesTable from "../../components/tables/DashboardUnpaidSamplesTable";
import UnpaidsamplePickupPopup from "../projects/unpaid-sample/UnpaidsamplePickupPopup";
import { UnpaidSamplesReturnInfo } from "../../../models/ProjectManagement/UnpaidSample";
import AppSearchDropDown2 from "../../components/common/filter/AppSearchDropdown2";

const UNPAID_SAMPLES_ANALYTICS_FILTERS_KEY =
  "UNPAID_SAMPLES_ANALYTICS_FILTERS_KEY";

const archivedOptionsList = [
  { id: "1", value: "Active" },
  { id: "2", value: "Archived" },
];

const numberOfDaysOptions = [
  { id: "0", value: "Select" },
  { id: "-1", value: "Shipping Info Unavailable" },
  { id: "10", value: "Shipped > 10 Days" },
  { id: "20", value: "Shipped > 20 Days" },
  { id: "30", value: "Shipped > 30 Days" },
];

const UnpaidSamplesAnalyticsDashboard: React.FC = () => {
  const theme = useTheme();

  let apiTimer: any;
  const {
    unpaidSamplesAnalyticsDashboardStore,
    projectStore,
    employeeStore,
    userStore,
    projectManagementStore,
    orderStore,
  } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiltersData, setSelectedFiltersData] = useState<FilterValue[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [reloadDataList, setReloadDataList] = useState(false);
  // const [reloadList, setReloadList] = useState(false);
  const { showError, showSuccess } = useNotification();
  const [unpaidSampleActionPopup, setUnpaidSampleActionPopup] =
    useState<boolean>(false);

  const initialFilters: UnpaidSamplesAnalyticsFilters =
    parseQueryParamsForUnpaidSamplesAnalyticsFilters(
      // localStorage.getItem(UNPAID_SAMPLES_ANALYTICS_FILTERS_KEY)
      //   ? JSON.parse(
      //       localStorage.getItem(UNPAID_SAMPLES_ANALYTICS_FILTERS_KEY)!
      //     )
      //   : null,
      null,
      searchParams
    );

  const [filters, setFilters] =
    useState<UnpaidSamplesAnalyticsFilters>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<UnpaidSamplesAnalyticsSelectedFilters>({
      ...initialFilters,
      start_date: initialFilters.start_date.format("YYYY-MM-DD"),
      end_date: initialFilters.end_date.format("YYYY-MM-DD"),
    });

  useEffect(() => {
    updateSelectedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reloadDataList) reloadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            if (key === "archived") {
              newSearchParams.set(
                key,
                archivedOptionsList.find(
                  (item) =>
                    item.value.toLowerCase() ===
                    value[0].value.toLocaleLowerCase()
                )?.value || "Active"
              );
            } else {
              newSearchParams.set(key, value.map((item) => item.id).join(","));
            }
          }
        } else {
          newSearchParams.set(key, value);
        }
      }
    });

    setSearchParams(newSearchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  const updateSelectedFilters = () => {
    localStorage.setItem(
      UNPAID_SAMPLES_ANALYTICS_FILTERS_KEY,
      JSON.stringify(filters)
    );

    const start_date = filters.start_date.format("YYYY-MM-DD");
    const end_date = filters.end_date.format("YYYY-MM-DD");

    if (
      selectedFilters.page_no !== filters.page_no ||
      selectedFilters.sort_by !== filters.sort_by ||
      selectedFilters.sort_type !== filters.sort_type ||
      selectedFilters.limit !== filters.limit
    ) {
      setReloadDataList(true);
    } else {
      setReloadDataList(false);
    }

    setSelectedFilters((_) => ({
      ...filters,
      start_date: start_date,
      end_date: end_date,
    }));

    const newFilters: React.SetStateAction<FilterValue[]> = [];

    if (filters.sales_admin_id) {
      filters.sales_admin_id.forEach((sales_admin_id) =>
        newFilters.push({ id: "sales_admin_id", value: sales_admin_id.value })
      );
    }

    if (filters.archived) {
      filters.archived.forEach((archived) =>
        newFilters.push(
          archivedOptionsList.find(
            (item) =>
              item.value.toLowerCase() === archived.value.toLocaleLowerCase()
          ) || { id: "1", value: "Active" }
        )
      );
    }

    if (filters.pending_since) {
      filters.pending_since.forEach((pending_since) =>
        newFilters.push(
          numberOfDaysOptions.find(
            (item) =>
              item.value.toLowerCase() ===
              pending_since.value.toLocaleLowerCase()
          ) || { id: "10", value: "10 Days" }
        )
      );
    }

    setSelectedFiltersData(newFilters);
  };

  useEffect(() => {
    projectStore.getTypeOfOrder();
    employeeStore
      .fetchEmployee({
        page_no: 1,
        limit: 30,
        sortBy: "name",
        sortType: SortType.ASC,
        search_text: undefined,
        team_id: { id: "2", value: "Sales" },
      })
      .catch((err) => {
        showError("Error", err?.response?.data?.message);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = async () => {
    setLoading(true);

    try {
      setFilters((prev) => ({
        ...prev,
        search: "",
        sort_by: "o.created_date",
        sort_type: SortType.DESC,
      }));

      await unpaidSamplesAnalyticsDashboardStore.getTats({
        page_no: selectedFilters.page_no,
        limit: selectedFilters.limit,
        sales_admin_id: selectedFilters.sales_admin_id,
        archived: selectedFilters.archived,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
        sort_by: selectedFilters.sort_by,
        sort_type: selectedFilters.sort_type,
        order_id: selectedFilters.order_id,
        pending_since: selectedFilters.pending_since,
      });

      await unpaidSamplesAnalyticsDashboardStore.getUnpaidSamples({
        page_no: selectedFilters.page_no,
        limit: selectedFilters.limit,
        sales_admin_id: selectedFilters.sales_admin_id,
        archived: selectedFilters.archived,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
        sort_by: selectedFilters.sort_by,
        sort_type: selectedFilters.sort_type,
        order_id: selectedFilters.order_id,
        pending_since: selectedFilters.pending_since,
      });
    } catch (error) {
      showError("Error", "Failed to fetch the analytics dashboard details 2.");
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const reloadList = async () => {
    try {
      setLoading(true);
      await unpaidSamplesAnalyticsDashboardStore.getUnpaidSamples({
        page_no: selectedFilters.page_no,
        limit: selectedFilters.limit,
        sales_admin_id: selectedFilters.sales_admin_id,
        archived: selectedFilters.archived,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
        sort_by: selectedFilters.sort_by,
        sort_type: selectedFilters.sort_type,
        order_id: selectedFilters.order_id,
        pending_since: selectedFilters.pending_since,
      });
    } catch (error) {
      showError("Error", "Failed to fetch the analytics dashboard details 1.");
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    if (range.startDate && range.endDate) {
      setFilters((prev) => ({
        ...prev,
        start_date: range.startDate!,
        end_date: range.endDate!,
      }));
    }
  };

  const handleRemoveFilter = (index: number) => {
    const filterToRemove = selectedFiltersData[index];
    const updatedFilters = selectedFiltersData.filter((_, i) => i !== index);
    setSelectedFiltersData(updatedFilters);

    switch (filterToRemove.id) {
      case "sales_admin_id":
        setFilters((prev) => ({
          ...prev,
          sales_admin_id:
            filters.sales_admin_id?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;

      case "archived":
        setFilters((prev) => ({
          ...prev,
          archived:
            prev.archived?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;
      case "pending_since":
        setFilters((prev) => ({
          ...prev,
          pending_since:
            prev.pending_since?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;
      case "order_id":
        setFilters((prev) => ({
          ...prev,
          order_id:
            prev.order_id?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;
      default:
        break;
    }
  };

  const handleClearAllFilters = () => {
    setFilters((_) => ({
      ...initialFilters,
      pm_adminids: [],
      order_id: [],
      order_type: [],
      archived: [{ id: "1", value: "Active" }],
      pending_since: null,
    }));

    setSelectedFiltersData([]);
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setFilters((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleUpdateUnpaidSamplePickupInfoUpdate = async (
    order_id: string,
    pickup_info: UnpaidSamplesReturnInfo
  ) => {
    setLoading(true);
    return projectManagementStore
      .updateUnpaidSamplePickupDate(order_id, pickup_info)
      .then(async (apiResponse) => {
        if (
          apiResponse &&
          projectManagementStore.unpaidSamplePickupInfoResponce
        ) {
          const existing =
            projectManagementStore.unpaidSamplePickupInfoResponce;
          const updatedData = {
            ...existing,
            ...apiResponse,
            order_id: existing.order_id ?? Number(order_id),
            id: existing.id ?? apiResponse.id,
            image_url: apiResponse.image_url,
            image_urls:
              apiResponse.image_urls ??
              (apiResponse.image_url
                ? apiResponse.image_url.map((url: string, i: number) => ({
                    id: i,
                    image_url: url,
                  }))
                : undefined),
            sample_received_at_office: apiResponse.sample_received_at_office,
            modified_by_name:
              userStore.user?.name ?? apiResponse.modified_by_name,
            modified_sales_poc_id:
              userStore.user?.id ?? apiResponse.modified_sales_poc_id,
          };
          projectManagementStore.updateSampleReturnInfoResponse(updatedData);
          await projectManagementStore.getUnpaidSampleReturnInfoById(
            Number(order_id)
          );
        }
        showSuccess("Unpaid sample details updated successfully");
        setUnpaidSampleActionPopup(false);
      })
      .catch((error) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            error?.message ||
            "Failed to update unpaid sample details"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleOrderIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 0)
      apiTimer = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch((err) => {});
      }, 1500);
  };

  useEffect(() => {
    if (unpaidSampleActionPopup) {
      const currentOrderId =
        unpaidSamplesAnalyticsDashboardStore.selectedOrderId;

      if (
        !projectManagementStore.unpaidSamplePickupInfoResponce ||
        projectManagementStore.unpaidSamplePickupInfoResponce.order_id !==
          Number(currentOrderId)
      ) {
        projectManagementStore.getUnpaidSampleReturnInfoById(
          Number(currentOrderId)
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    unpaidSampleActionPopup,
    unpaidSamplesAnalyticsDashboardStore.selectedOrderId,
  ]);

  return (
    <>
      {userStore.hasPermission(EmployeeRoles.VIEW_UNPAID_SAMPLES_DASHBOARD) ? (
        <>
          <LoaderOverlay loading={loading} />
          <Box
            sx={{
              margin: "8px",
              gap: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: theme.palette.customColors.white,
                padding: 4,
                borderRadius: 2,
                gap: 2,
              }}
            >
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                <Grid2 size={{ xl: 2, lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Date Range
                  </CustomTypography>
                  <DateRangePickerNew
                    onRangeSelect={handleRangeSelect}
                    startDate={filters.start_date}
                    endDate={filters.end_date}
                  />
                </Grid2>
                <Grid2 size={{ xl: 2, lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Sales POC"}
                  </CustomTypography>
                  <Box>
                    <AppDropDown
                      title=""
                      isMultiSelect={true}
                      valuesList={
                        filters.sales_admin_id?.map((item) => item.id) || []
                      }
                      list={
                        employeeStore.employees.map((item) => ({
                          id: item.id?.toString() || "",
                          value: item.name || "",
                        })) || []
                      }
                      fontSize={"12px"}
                      onMultiSelectionChange={(ids) => {
                        setFilters((prev) => ({
                          ...prev,
                          sales_admin_id:
                            employeeStore.employees
                              ?.filter((item) =>
                                ids.includes(item.id?.toString() || "")
                              )
                              ?.map((item) => ({
                                id: item.id?.toString() || "",
                                value: item.name || "",
                              })) || [],
                        }));
                      }}
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xl: 2, lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Project Status"}
                  </CustomTypography>
                  <AppDropDown
                    title=""
                    fontSize="0.7rem"
                    isMultiSelect={false}
                    value={filters.archived?.[0]?.id}
                    list={archivedOptionsList}
                    onSingleSelectionChange={(id) => {
                      const selected = archivedOptionsList.find(
                        (item) => item.id === id
                      );
                      if (selected) {
                        setFilters((prev) => ({
                          ...prev,
                          archived: [selected],
                        }));
                      } else {
                        setFilters((prev) => ({
                          ...prev,
                          archived: [],
                        }));
                      }
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xl: 2, lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Order ID
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Order Id"
                    value={filters.order_id || []}
                    fontSize="0.9rem"
                    list={orderStore.orderIds.map((item) => {
                      return { id: String(item), value: String(item) };
                    })}
                    local={false}
                    handleSearch={handleOrderIdSearch}
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        order_id: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xl: 2, lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Shipped Since"}
                  </CustomTypography>
                  <AppDropDown
                    title=""
                    fontSize="0.7rem"
                    isMultiSelect={false}
                    value={filters.pending_since?.[0]?.id || "0"}
                    list={numberOfDaysOptions}
                    onSingleSelectionChange={(id) => {
                      const selected = numberOfDaysOptions.find(
                        (item) => item.id === id
                      );
                      if (selected) {
                        setFilters((prev) => ({
                          ...prev,
                          pending_since: [selected],
                        }));
                      } else {
                        setFilters((prev) => ({
                          ...prev,
                          pending_since: [],
                        }));
                      }
                    }}
                  />
                </Grid2>
                <Grid2
                  size={{ xl: 2, lg: 3, md: 4, sm: 12, xs: 12 }}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    gap: 2,
                  }}
                >
                  <AppButton
                    variant="primaryBlock"
                    label="Search"
                    onClick={handleApply}
                    customStyles={{ width: "100%" }}
                    disabled={loading}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear"
                    customStyles={{ width: "100%" }}
                    onClick={() => handleClearAllFilters()}
                  />
                </Grid2>
              </Grid2>
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                <Grid2 size={{ lg: 2, md: 3, sm: 12, xs: 12 }}></Grid2>
                <Grid2 size={{ lg: 2, md: 3, sm: 12, xs: 12 }}></Grid2>
                <Grid2 size={{ lg: 2, md: 3, sm: 12, xs: 12 }}></Grid2>
                <Grid2 size={{ lg: 2, md: 3, sm: 12, xs: 12 }}></Grid2>
              </Grid2>

              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                {selectedFiltersData.length > 0 && (
                  <Box sx={{ paddingTop: "15px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        borderRadius: 2,
                      }}
                    >
                      {selectedFiltersData.map((filter, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor:
                              theme.palette.customColors.lightPurple,
                            borderRadius: "8px",
                            border: "1px solid",
                            borderColor: theme.palette.customColors.blackBorder,
                            height: "34px",
                            paddingX: "6px",
                          }}
                        >
                          <CustomTypography
                            variant="heading9"
                            color={theme.palette.customColors.text}
                            style={{ marginRight: "5px" }}
                          >
                            {filter.value}
                          </CustomTypography>
                          <Button
                            onClick={() => handleRemoveFilter(index)}
                            sx={{
                              width: "14px",
                              height: "14px",
                              minWidth: "14px",
                              minHeight: "14px",
                              fontSize: "15px",
                              padding: "0px",
                              border: "1px solid #E2E2E2",
                              borderRadius: "50%",
                              backgroundColor: theme.palette.customColors.white,
                              cursor: "pointer",
                              overflow: "hidden",
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.customColors.lightGreyColor,
                              },
                            }}
                          >
                            ×
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Grid2>
            </Box>
            <Box>
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.blackBorder}
              >
                <span style={{ fontStyle: "italic" }}>Note:</span> The analytics
                data is cached. Recent changes may take up to 30 minutes to
                reflect.
              </CustomTypography>
            </Box>
            <Box>
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                {unpaidSamplesAnalyticsDashboardStore.basic_tats.map(
                  (item, index) => (
                    <Box
                      key={`${item.label}-${index}`}
                      sx={{
                        flex: {
                          xs: "1 1 140px",
                          sm: "1 1 180px",
                          md: "1 1 200px",
                        },
                        maxWidth: {
                          xs: "calc(50% - 8px)",
                          sm: "calc(33.33% - 16px)",
                          md: "calc(25% - 16px)",
                          lg: "calc(20% - 16px)",
                          xl: "calc(16.66% - 16px)",
                        },
                        minWidth: { xs: "140px", sm: "160px", md: "180px" },
                      }}
                    >
                      <TypeFilterCard
                        title={item.label}
                        message={item.info}
                        value={item.value ?? "0"}
                        color={""}
                        icon={
                          "https://content.swageazy.com/icons/boxes_green.png"
                        }
                        onClick={(title) => {
                          switch (title) {
                            case "Total Orders":
                              const url = `/report/data?title=${encodeURI(
                                "Unpaid Samples"
                              )}&base_url=/v5/sales-analytics/unpaid-samples&path=/data&${searchParams.toString()}`;
                              window.open(url, "_blank");
                              break;
                            default:
                              break;
                          }
                        }}
                      />
                    </Box>
                  )
                )}
              </Grid2>
            </Box>
            <Box>
              {unpaidSamplesAnalyticsDashboardStore.unpaid_samples.list &&
                unpaidSamplesAnalyticsDashboardStore.unpaid_samples.list
                  .length > 0 && (
                  <DashboardUnpaidSamplesTable
                    unpaid_samples={
                      unpaidSamplesAnalyticsDashboardStore.unpaid_samples.list
                    }
                    limit={filters.limit || 10}
                    maxHeight={600}
                    onPageChange={(newPage: number) => {
                      setFilters((prev) => ({ ...prev, page_no: newPage }));
                    }}
                    page_no={selectedFilters.page_no}
                    total_pages={
                      unpaidSamplesAnalyticsDashboardStore.unpaid_samples
                        .total_pages
                    }
                    total_count={
                      unpaidSamplesAnalyticsDashboardStore.unpaid_samples
                        .total_count
                    }
                    onSortChange={(column, sortType) => {
                      setFilters((prev) => ({
                        ...prev,
                        sort_by: column,
                        sort_type: sortType,
                      }));
                    }}
                    sortBy={selectedFilters.sort_by}
                    sortType={selectedFilters.sort_type}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    onActionClick={(id) => {
                      unpaidSamplesAnalyticsDashboardStore.setSelectedOrderId(
                        id
                      );
                      setUnpaidSampleActionPopup(true);
                    }}
                  />
                )}
              {userStore.hasPermission(
                EmployeeRoles.EDIT_UNPAID_RETURN_SAMPLES_PICKUP_INFO
              ) && (
                <UnpaidsamplePickupPopup
                  order_id={
                    unpaidSamplesAnalyticsDashboardStore.selectedOrderId
                  }
                  pickUpdata={
                    projectManagementStore.unpaidSamplePickupInfoResponce
                  }
                  open={unpaidSampleActionPopup}
                  onClose={() => {
                    setUnpaidSampleActionPopup(false);
                    unpaidSamplesAnalyticsDashboardStore.setSelectedOrderId("");
                    projectManagementStore.resetUnpaidSamplePickupFormData();
                  }}
                  onSave={(data) => {
                    return handleUpdateUnpaidSamplePickupInfoUpdate(
                      unpaidSamplesAnalyticsDashboardStore.selectedOrderId,
                      data
                    );
                  }}
                />
              )}
              {!unpaidSamplesAnalyticsDashboardStore.unpaid_samples.list ||
                (unpaidSamplesAnalyticsDashboardStore.unpaid_samples.list
                  .length === 0 && <p>No Data</p>)}
            </Box>
          </Box>
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </>
  );
};

export default observer(UnpaidSamplesAnalyticsDashboard);
